import { IMAGE_URLS } from '@/constants/constants';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const AlertFill = IMAGE_URLS.common?.alertFill;

// Toast Context
const ToastContext = createContext<any>(null);

// Toast types
const TOAST_TYPES = {
	SUCCESS: 'success',
	ERROR: 'error',
	INFO: 'info'
};

// Position types
const TOAST_POSITIONS = {
	TOP_LEFT: 'top-left',
	TOP_CENTER: 'top-center',
	TOP_RIGHT: 'top-right',
	BOTTOM_LEFT: 'bottom-left',
	BOTTOM_CENTER: 'bottom-center',
	BOTTOM_RIGHT: 'bottom-right'
};

// SVG Icons
const CheckIcon = ({ color = "#5FE55C" }: { color?: string }) => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M10 19C8.755 19 7.585 18.7638 6.49 18.2913C5.395 17.8188 4.4425 17.1775 3.6325 16.3675C2.8225 15.5575 2.18125 14.605 1.70875 13.51C1.23625 12.415 1 11.245 1 10C1 8.755 1.23625 7.585 1.70875 6.49C2.18125 5.395 2.8225 4.4425 3.6325 3.6325C4.4425 2.8225 5.395 2.18125 6.49 1.70875C7.585 1.23625 8.755 1 10 1C11.245 1 12.415 1.23625 13.51 1.70875C14.605 2.18125 15.5575 2.8225 16.3675 3.6325C17.1775 4.4425 17.8188 5.395 18.2913 6.49C18.7638 7.585 19 8.755 19 10C19 11.245 18.7638 12.415 18.2913 13.51C17.8188 14.605 17.1775 15.5575 16.3675 16.3675C15.5575 17.1775 14.605 17.8188 13.51 18.2913C12.415 18.7638 11.245 19 10 19Z" fill={color}/>
		<path d="M13.7155 7C13.8953 7 14.0623 7.03135 14.2164 7.09406C14.3706 7.15677 14.5076 7.24666 14.6275 7.36371C14.7474 7.48077 14.8373 7.61246 14.8972 7.75878C14.9572 7.9051 14.9914 8.07023 15 8.25418C15 8.4214 14.9679 8.58236 14.9037 8.73704C14.8394 8.89172 14.7474 9.02759 14.6275 9.14465L9.52794 14.13C9.40805 14.2471 9.26889 14.337 9.11047 14.3997C8.95204 14.4624 8.7872 14.4958 8.61593 14.5C8.44466 14.5 8.27981 14.4686 8.12139 14.4059C7.96296 14.3432 7.82381 14.2513 7.70392 14.13L5.37893 11.8599C5.25905 11.7429 5.16699 11.607 5.10276 11.4523C5.03854 11.2977 5.00428 11.1367 5 10.9695C5 10.7939 5.03425 10.6309 5.10276 10.4804C5.17127 10.3298 5.26333 10.1961 5.37893 10.079C5.49454 9.96196 5.62942 9.87416 5.78356 9.81564C5.9377 9.75711 6.10683 9.72366 6.29094 9.7153C6.46221 9.7153 6.62706 9.74666 6.78548 9.80936C6.94391 9.87207 7.08307 9.96196 7.20295 10.079L8.61593 11.4586L12.8035 7.36371C12.9234 7.24666 13.0625 7.15677 13.2209 7.09406C13.3794 7.03135 13.5442 7 13.7155 7Z" fill="#172218"/>
	</svg>
);

const CloseIcon = ({ color = "#5FE55C" }: { color?: string }) => (
	<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path fillRule="evenodd" clipRule="evenodd" d="M4.42462 4.42462C4.76684 4.08242 5.32167 4.08242 5.66389 4.42462L10.0013 8.76204L14.3387 4.42462C14.6809 4.08242 15.2357 4.08242 15.578 4.42462C15.9202 4.76684 15.9202 5.32167 15.578 5.66388L11.2405 10.0013L15.578 14.3386C15.9202 14.6809 15.9202 15.2357 15.578 15.578C15.2357 15.9202 14.6809 15.9202 14.3387 15.578L10.0013 11.2405L5.66389 15.578C5.32167 15.9202 4.76684 15.9202 4.42462 15.578C4.08242 15.2357 4.08242 14.6809 4.42462 14.3386L8.76205 10.0013L4.42462 5.66388C4.08242 5.32167 4.08242 4.76684 4.42462 4.42462Z" fill={color}/>
	</svg>
);

const AlertFillIcon = ({ color = "#5FE55C" }: { color?: string }) => (
	<img
		src={AlertFill}
		alt="Error"
		className={`left-4 top-4`}
		width={20}
		height={20}
	/>
);

interface Toast {
	id: number;
	message: string;
	type: string;
	duration?: number;
	isExiting?: boolean;
	uniqueId?: string; // Optional unique identifier to prevent duplicates
}

interface ToastContextType {
	addToast: (message: string, type?: string, duration?: number, uniqueId?: string) => number;
	removeToast: (id: number) => void;
	removeAllToasts: () => void;
	toasts: Toast[];
	position: string;
	changePosition: (position: string) => void;
}

// Individual Toast Component
const Toast = ({ toast, onRemove, position }: { toast: Toast; onRemove: (id: number) => void; position: string }) => {
	const [isExiting, setIsExiting] = useState(false);

	useEffect(() => {
		
		const timer = setTimeout(() => {
			// Start exit animation
			setIsExiting(true);
			
			// Remove toast after animation completes
			setTimeout(() => {
				onRemove(toast.id);
			}, 300); // Match this with animation duration
		}, toast.duration);

		return () => clearTimeout(timer);
	}, [toast.id, toast.duration, onRemove]);

	const getIcon = () => {
		switch (toast.type) {
		case TOAST_TYPES.SUCCESS:
			return <CheckIcon />;
		case TOAST_TYPES.ERROR:
			return <AlertFillIcon />;
		default:
			return null;
		}
	};

	const getBackgroundStyle = () => {
		switch (toast.type) {
		case TOAST_TYPES.SUCCESS:
			return {
				background: 'radial-gradient(50% 50% at 50% 50%, rgba(95, 229, 92, 0.15) 0%, rgba(95, 229, 92, 0.08) 100%)',
				backdropFilter: 'blur(25px)'
			};
		case TOAST_TYPES.ERROR:
			return {
				background: 'radial-gradient(50% 50% at 50% 50%, rgba(255, 128, 128, 0.15) 0%, rgba(255, 128, 128, 0.08) 100%)',
				backdropFilter: 'blur(25px)'
			};
		default:
			return {
				background: 'radial-gradient(50% 50% at 50% 50%, rgba(95, 229, 92, 0.15) 0%, rgba(95, 229, 92, 0.08) 100%)',
				backdropFilter: 'blur(25px)'
			};
		}
	};

	const getTextColor = () => {
		switch (toast.type) {
		case TOAST_TYPES.SUCCESS:
			return '#5FE55C';
		case TOAST_TYPES.ERROR:
			return '#FF8080';
		default:
			return '#5FE55C';
		}
	};

	const getTextShadow = () => {
		switch (toast.type) {
		case TOAST_TYPES.SUCCESS:
			return '0 0 20px rgba(95, 229, 92, 0.20)';
		case TOAST_TYPES.ERROR:
			return '0 0 20px rgba(239, 68, 68, 0.20)';
		default:
			return '0 0 20px rgba(95, 229, 92, 0.20)';
		}
	};

	const getAnimationClass = () => {
		if (isExiting) {
			// Exit animation - always slide down regardless of position
			return 'animate-out slide-out-to-bottom-full duration-300 ease-in';
		}
		
		// Entry animations based on position
		switch (position) {
			case TOAST_POSITIONS.TOP_LEFT:
			case TOAST_POSITIONS.BOTTOM_LEFT:
				return 'animate-in slide-in-from-left-full duration-300 ease-out';
			case TOAST_POSITIONS.TOP_RIGHT:
			case TOAST_POSITIONS.BOTTOM_RIGHT:
				return 'animate-in slide-in-from-right-full duration-300 ease-out';
			case TOAST_POSITIONS.TOP_CENTER:
				return 'animate-in slide-in-from-top-full duration-300 ease-out';
			case TOAST_POSITIONS.BOTTOM_CENTER:
				return 'animate-in slide-in-from-bottom-full duration-300 ease-out';
			default:
				return 'animate-in slide-in-from-bottom-full duration-300 ease-out';
		}
	};

	const handleManualClose = () => {
		setIsExiting(true);
		setTimeout(() => {
			onRemove(toast.id);
		}, 300);
	};

	return (
		<div
			className={`flex items-start justify-between p-4 rounded-xl min-w-fit pointer-events-auto ${getAnimationClass()}`}
			style={getBackgroundStyle()}
			onClick={(e) => e.stopPropagation()}
		>
			<div className='flex flex-col gap-2 justify-start'>
				{getIcon()}
				<span
					className="text-[16px] flex-1 font-medium leading-5 break-words text-wrap max-w-[500px]"
					style={{
						color: getTextColor(),
						textShadow: getTextShadow(),
						fontFamily: 'Brockmann, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
						fontSize: '16px',
						fontWeight: '500',
						lineHeight: '20px',
						letterSpacing: '-0.2px'
					}}
					>
					{toast.message}
				</span>
			</div>
			<button
				title="Close"
				onClick={handleManualClose}
				className="flex-shrink-0 ml-6 transition-opacity cursor-pointer pointer-events-auto opacity-60 hover:opacity-80"
				type="button"
			>
				<CloseIcon color={getTextColor()} />
			</button>
		</div>
	);
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast, position }: { toasts: Toast[]; removeToast: (id: number) => void; position: string }) => {
	if (toasts.length === 0) return null;

	const getPositionClasses = () => {
		switch (position) {
			case TOAST_POSITIONS.TOP_LEFT:
				return 'fixed top-4 left-4 z-[99999] flex flex-col gap-3 max-md:left-2 max-md:right-2 max-md:w-auto';
			case TOAST_POSITIONS.TOP_CENTER:
				return 'fixed top-4 left-1/2 transform -translate-x-1/2 z-[99999] flex flex-col gap-3 max-md:left-2 max-md:right-2 max-md:transform-none max-md:translate-x-0';
			case TOAST_POSITIONS.TOP_RIGHT:
				return 'fixed top-4 right-4 z-[99999] flex flex-col gap-3 max-md:left-2 max-md:right-2';
			case TOAST_POSITIONS.BOTTOM_LEFT:
				return 'fixed bottom-4 left-4 z-[99999] flex flex-col-reverse gap-3 max-md:left-2 max-md:right-2 max-md:w-auto';
			case TOAST_POSITIONS.BOTTOM_CENTER:
				return 'fixed bottom-4 left-1/2 transform z-[99999] -translate-x-1/2 z-[9999] flex flex-col-reverse gap-3 max-md:left-2 max-md:right-2 max-md:transform-none max-md:translate-x-0';
			case TOAST_POSITIONS.BOTTOM_RIGHT:
				return 'fixed bottom-4 right-4 z-[99999] flex flex-col-reverse gap-3 max-md:left-2 max-md:right-2';
			default:
				return 'fixed bottom-4 left-1/2 z-[99999] transform -translate-x-1/2 z-[9999] flex flex-col-reverse gap-3 max-md:left-2 max-md:right-2 max-md:transform-none max-md:translate-x-0';
		}
	};

	return createPortal(
		<div className={getPositionClasses()}>
		{toasts.map((toast) => (
			<Toast key={toast.id} toast={toast} onRemove={removeToast} position={position} />
		))}
		</div>,
		document.body
	);
};

// Toast Provider Component
export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const [position, setPosition] = useState(TOAST_POSITIONS.BOTTOM_LEFT);

	const addToast = (message: string, type: string = TOAST_TYPES.SUCCESS, duration: number = 2000, uniqueId?: string): number => {
		const id = Date.now() + Math.random();
		const newToast: Toast = { id, message, type, duration, uniqueId };

		setToasts((prev) => {
			// If uniqueId is provided, check if a toast with the same uniqueId already exists
			if (uniqueId) {
				const existingToastIndex = prev.findIndex(toast => toast.uniqueId === uniqueId);

				if (existingToastIndex !== -1) {
					// Replace the existing toast with the new one
					const updatedToasts = [...prev];
					updatedToasts[existingToastIndex] = newToast;
					return updatedToasts;
				}
			}

			// Add new toast if no uniqueId or no duplicate exists
			return [...prev, newToast];
		});

		return id;
	};

	const removeToast = (id: number) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	};

	const removeAllToasts = () => {
		setToasts([]);
	};

	const changePosition = (newPosition: string) => {
		setPosition(newPosition);
	};

	const value: ToastContextType = {
		addToast,
		removeToast,
		removeAllToasts,
		toasts,
		position,
		changePosition
	};

	return (
		<ToastContext.Provider value={value}>
		{children}
		<ToastContainer toasts={toasts} removeToast={removeToast} position={position} />
		</ToastContext.Provider>
	);
};

// Custom Hook
export const useNewToast = () => {
	const context = useContext(ToastContext);
	
	if(!context) {
		throw new Error('useNewToast must be used within a ToastProvider');
	}

	const { addToast, removeToast, removeAllToasts, changePosition } = context;

	return {
		newToast: {
			success: (message: string, duration?: number, uniqueId?: string) => addToast(message, TOAST_TYPES.SUCCESS, duration, uniqueId),
			error: (message: string, duration?: number, uniqueId?: string) => addToast(message, TOAST_TYPES.ERROR, duration, uniqueId),
			info: (message: string, duration?: number, uniqueId?: string) => addToast(message, TOAST_TYPES.INFO, duration, uniqueId)
		},
		removeToast,
		removeAllToasts,
		changePosition
	};
};

// Export position constants for easy use
export { TOAST_POSITIONS };